/*
 * Copyright (c) 2023. All rights are reserved to Newport Collection
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Button, Grid, List, Typography } from '@material-ui/core';
import { useTranslation } from 'tools/i18n';
import { useRouter } from 'next/router';
import { TrackingContext } from 'components/providers/tracking';
import SearchIcon from 'public/images/icons/search-thin.svg';
import CloseIcon from "public/images/icons/close.svg";
import elevateSearch from '../methods/elevateSearch';
import { getFallbackSuggestions, getLiveSuggestions } from '../methods/getSuggestions';
import { SearchResultBtn, SearchResultProduct, SearchSuggestion } from '../parts';
import coreStyle from '../style/coreStyle';
import useSessionStore from '@services/stores/SessionStore';

const useStyles = makeStyles(() => coreStyle);
let inputTimeout;
let focusTimeout;

export default function SearchCore({ center = false, show = true, mobile = false, searchFocus = false, right = false }) {
	const router = useRouter();
	const searchInputRef = useRef();
	const classes = useStyles();
	const { t } = useTranslation();
	const { actions } = useContext(TrackingContext);
	const { config, salesChannel } = useSessionStore((state) => state.session);
	const [scrollY, setScrollY] = useState(0);
	const onScroll = useCallback(event => { setScrollY(window.scrollY); },[scrollY]);
	const [state, setState] = useState({
		focus: false,
		dirty: false,
		products: [],
		suggestions: [],
		hitCount: 0,
		isMobile: mobile
	});
	const { focus, dirty, products, suggestions, hitCount, isMobile } = state;
	const [query, setQuery] = useState('');
	const [suggestedItems, setSuggestedItems] = useState([]);
	const glued = !router.asPath?.includes('sok') ? 'glued' : '';

	useEffect(() => {
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => { window.removeEventListener('scroll', onScroll, { passive: true }) };
	}, []);

	useEffect(async () => {
		if (suggestedItems.length === 0) {
			await getLiveSuggestions(salesChannel).then(res => {
				setSuggestedItems(res);
			});
		}
	}, [salesChannel]);

	useEffect(async () => {
		setState({ ...state, focus: searchFocus });
	}, [searchFocus]);

	useEffect(() => {
		setState({ ...state, focus: false });
	}, [router]);

	useEffect(() => {
		if (scrollY > 200 && !mobile) {
			setState({ ...state, focus: false });
			searchInputRef.current.blur();
		}
	}, [scrollY]);

	useEffect(async () => { onFocus(); }, [focus]);

	useEffect(async () => {
		if (focus && !dirty && suggestedItems.length === 0) {
			await getLiveSuggestions(salesChannel).then(res => {
				setSuggestedItems(res);
			});
		}
	}, [focus, dirty]);

	useEffect(() => {
		if (query.length > 2) {
			setState(state => {
				return { ...state, dirty: true };
			});
		} else {
			setState(state => {
				return { ...state, products: [], suggestions: [], dirty: false };
			});
		}
	}, [query]);

	function onChange(e) {
		const query = e.target.value;
		setQuery(query);

		if (!focus) {
			setState({ ...state, focus: true });
		}

		clearTimeout(inputTimeout);
		if (query.length > 2) {
			inputTimeout = setTimeout(async () => {
				await elevateSearch(query, config, mobile, state, setState);
			}, 200);
		}
	}

	function onFocus(isFocused) {
		isFocused = isFocused || focus;
		if (isFocused) {
			actions.click({
				type: 'open_search',
				text: t('search'),
				identifier: router.asPath
			});
			clearTimeout(focusTimeout);
			focusTimeout = setTimeout(() => {
				searchInputRef.current.focus();
			}, 150);
		} else if (!mobile) {
			searchInputRef.current.blur();
		}
	}

	function onKeyDown(e) {
		if (e.key === 'Enter') {
			goToSearchResults();
		}
	}

	function goToSearchResults() {
		if (query.length > 2) {
			router.push({
				pathname: t('search-url'),
				query: { q: query }
			}).then();
		} else if (!mobile) {
			setState({ ...state, focus: !focus });
		}
	}

	function clearSearch() {
		searchInputRef.current.focus();
		setQuery('');
	}

	function SearchBtn() {
		return (
			<Button
				onClick={goToSearchResults}
				className={[classes.icon__button, glued, classes.icon__button_search, focus ? 'focus' : ''].join(' ')}
			>
				<SearchIcon className={[classes.search__icon, focus ? 'focus' : ''].join(' ')} />
			</Button>
		);
	}

	function ClearBtn() {
		return dirty ? (
			<Button
				onClick={() => clearSearch()}
				className={[classes.icon__button, classes.icon__button_close, focus ? 'focus' : ''].join(' ')}
			>
				<CloseIcon className={[classes.close__icon, focus ? 'focus' : ''].join(' ')} />
			</Button>
		) : ('');
	}

	const wrapperClasses = [
		classes.search__wrapper,
		right ? 'right' : '',
		focus ? 'focus' : '',
		dirty ? 'dirty' : '',
		show ? '' : 'hide'
	].join(' ');

	const autocompleteClasses = [
		classes.autocomplete,
		glued,
		focus || mobile ? 'focus' : '',
		center ? 'center' : '',
		right ? 'right' : ''
	].join(' ');

	return (
		<>
			<div
				className={[classes.obscure, focus && !mobile ? 'show' : ''].join(' ')}
				onClick={() => setState({ ...state, focus: false })}
			/>
			<div className={wrapperClasses}>
				<div className={[classes.search__inner, glued].join(' ')}>
					<input
						ref={searchInputRef}
						type="text"
						placeholder={t('search')}
						name="search"
						value={query}
						onChange={onChange}
						onKeyDown={onKeyDown}
						autoComplete="one-time-code"
						autoCorrect="off"
						className={[classes.search__input, glued, focus || isMobile ? 'focus' : ''].join(' ')}
						onFocus={() => {
							setState({ ...state, focus: true });
						}}
					/>
					<SearchBtn />
					<ClearBtn />
				</div>

				<div className={autocompleteClasses}>
					<Grid container>
						<Grid xs={12} className={classes.suggestions__wrapper} item>
							{suggestions.length > 0 ? (
								<List disablePadding>
									{suggestions.map((suggestion, index) => (
										<SearchSuggestion props={{ suggestion, index, query, classes, t, focus }} />
									))}
								</List>
							) : query.length > 2 ? (
								<Typography className={classes.noResultsText} variant="body1">
									{t('no-suggestions')}
								</Typography>
							) : (
								<>
									<Typography className={classes.relatedSearches} variant="body1">{t('search-popular-searches')}</Typography>
									<List disablePadding>
										{getFallbackSuggestions(salesChannel).map((suggestion, index) => (
											<SearchSuggestion key={index} props={{ suggestion, index, query, classes, t, focus }} />
										))}
									</List>
								</>
							)}
						</Grid>
						<Grid xs={12} item>
							{products.length > 0 ? (
								<Grid className={classes.products__wrapper} spacing={1} container>
									{products.map((product, index) => (
										<SearchResultProduct key={product.id} props={{ product, index, classes, config, focus, isMobile: mobile }} />
									))}
								</Grid>
							) : query.length > 2 ? (
								<Typography className={classes.noResultsText} variant="body1">
									{t('search-result-no-hit')}
								</Typography>
							) : suggestedItems.length > 0 ? (
								<Grid className={classes.products__wrapper} spacing={1} container>
									{suggestedItems.map((product, index) => (
										<SearchResultProduct key={product.id} props={{ product, index, classes, config, focus }} />
									))}
								</Grid>
							) : (
								''
							)}

							{hitCount > 6 && query ? <SearchResultBtn query={query} classes={classes} /> : ''}
						</Grid>
					</Grid>
				</div>
			</div>
		</>
	);
}
